import {Injectable} from '@angular/core';
import {AngularFirestore} from 'angularfire2/firestore';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import * as moment from 'moment';
import {FirebaseStorageService} from './firebase-storage.service';
import {RepairWithID} from '../../interfaces/repair.interface';
import {FirebaseNotesService} from './notes.service';


@Injectable()
export class FirebaseRepairService {

  constructor(private afs: AngularFirestore,
              private storeService: FirebaseStorageService,
              private noteService: FirebaseNotesService) {
  }


  /**
   *
   * @param {string} uid
   * @returns {Observable<DocumentChangeAction[]>}
   */
  getRepairsPerProperty(org_id: string, property_id: string): Observable<any> {
    const list = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id).collection('repairs');

    return list.snapshotChanges();
  }


  /**
   *
   * @param {string} uid
   * @returns {Observable<DocumentChangeAction[]>}
   */
  getRepairsPerPropertyByUid(org_id: string, property_id: string, uid: string): Observable<any> {
    const list = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id).collection('repairs',
        repair => repair.where('createdBy', '==', uid));

    return list.snapshotChanges();
  }


  /**
   * Update Repair Object in DB @ [organizations > org_id > properties > property_id > repairs > repaird_id]
   * @param {string} org_id
   * @param {string} property_id
   * @param repair_id
   * @param {boolean} value
   * @returns {Observable<any>}
   */
  updateRepairStatus(org_id: string, property_id: string, repair_id, value: boolean) {
    const object = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id)
      .collection('repairs').doc(repair_id);

    const transaction = object.update({
      completed: value
    });

    return this.fromFirebaseAuthPromise(transaction);
  }


  /**
   * Add Repair to DB @ [organizations > org_id > properties > property_id > repairs]
   * @param {string} org_id
   * @param {string} property_id
   * @param {any} formData
   * @returns {Observable<firebase.firestore.DocumentReference>}
   */
  addRepairToProperty(org_id: string, property_id: string, formData: any, isTenant?: boolean): Observable<any> {
    const repairRef = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id).collection('repairs');

    console.log('Repair add: ', formData);
    // Create Check for Image  //
    const fileCheck = formData.image;

    // Set variables to match both repairs with and without image //
    let path = '';
    let image = false;
    let file;
    if (fileCheck) {
      // get File from Files //
      file = formData.image.files[0];
      path = org_id + '/' + property_id + '/repairs/' + file.name;
      image = true;
    }


    const uid = localStorage.getItem('uid');
    // Populate fields on Repair object //
    const transaction = repairRef.add({
      title: formData.title,
      description: formData.description,
      image: image,
      imagePath: path,
      created: moment().toISOString(),
      isTenant: isTenant,
      createdBy: uid,
      completed: false
    });

    if (fileCheck) {
      // Use this method if Image is available //
      let response;
      return this.fromFirebaseAuthPromise(transaction
        .then(res => response = res)
        .then(() => this.storeService.uploadFile(path, org_id, file))
        .then(() => {
          return response;
        })
      );
    } else {
      return this.fromFirebaseAuthPromise(transaction);
    }

  }


  /**
   * Update Repair to DB @ [organizations > org_id > properties > property_id > repairs > repair_id ]
   * @param {string} org_id
   * @param {string} property_id
   * @param {any} formData
   * @param {string} imagePath //
   * @returns {Observable<firebase.firestore.DocumentReference>}
   */
  updateRepairToProperty(org_id: string, property_id: string, formData: any,
                         repair_id: string, imageUpdated: boolean, imagePath?: string): Observable<any> {


    console.log('update repaire image: ', imageUpdated);
    console.log('Repair edit Value: ', org_id, property_id, formData, repair_id, imageUpdated, imagePath);

    const updateRepairRef = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id).collection('repairs').doc(repair_id);


    // Populate fields on Repair object //
    let transaction = updateRepairRef.update({
      title: formData.title,
      description: formData.description,
    });

    const fileCheck = formData.image;

    // Init variables  //
    let path = '';
    let image = false;
    let file;
    if (imageUpdated || fileCheck) {
      // get File from Files //
      file = formData.image.files[0];
      path = org_id + '/' + property_id + '/repairs/' + file.name;
      image = true;
      transaction = updateRepairRef.update({
        title: formData.title,
        description: formData.description,
        image: image,
        imagePath: path,
      });
    }

    if (imageUpdated) {
      console.log('Update repair with Image and Values: ', org_id, property_id, formData, repair_id, imageUpdated, imagePath);
      // Use this method if Image is available //
      return this.fromFirebaseAuthPromise(transaction
        .then(() => this.storeService.removeFile(imagePath))
        .then(() => this.storeService.uploadFile(path, org_id, file))
      );
    } else {
      // No Previous Image //
      if (fileCheck) {
        console.log('Update repair with Image and Values: ', org_id, property_id, formData, repair_id, imageUpdated, imagePath);
        // Use this method if Image is available //
        return this.fromFirebaseAuthPromise(transaction
          .then(() => this.storeService.uploadFile(path, org_id, file))
        );
      } else {
        // No Image //
        console.log('Update repair without Image and Values: ', org_id, property_id, formData, repair_id, imageUpdated, imagePath);
        return this.fromFirebaseAuthPromise(transaction);
      }
    }

  }


  /**
   * Update Repair to DB @ [organizations > org_id > properties > property_id > repairs > repair_id ]
   * @param {string} org_id
   * @param {string} property_id
   * @param {any} formData
   * @param {string} imagePath //
   * @returns {Observable<firebase.firestore.DocumentReference>}
   */
  deleteRepairFromProperty(org_id: string, property_id: string, repair: RepairWithID): Observable<any> {
    const deleteRepairRef = this.afs.collection<any>('organizations')
      .doc(org_id).collection('properties').doc(property_id).collection('repairs').doc(repair.id);

    const imageCheck = repair.image;
    const response = [];
    // Init variables  //
    if (imageCheck) {
      console.log('Delete repair with Image and Values');
      // Use this method if Image is available //
      return this.fromFirebaseAuthPromise(deleteRepairRef.delete()
        .then(() => this.storeService.removeFile(repair.imagePath))
        .then(() => this.noteService.deleteNoteByItemID(org_id, property_id, repair.id))
        .catch(error => {
          console.log(error);
          return error;
        })
      );
    } else {
      console.log('Delete repair without Image and Values: ');
      return this.fromFirebaseAuthPromise(deleteRepairRef.delete()
        .then(() => this.noteService.deleteNoteByItemID(org_id, property_id, repair.id))
        .catch(error => {
          console.log(error);
          return error;
        })
      );
    }
  }


  // Convert Promise of Firebase to an Observable
  fromFirebaseAuthPromise(promise): Observable<any> {

    const subject = new Subject<any>();

    promise
      .then(res => {
          subject.next(res);
          subject.complete();
        },
        err => {
          subject.error(err);
          subject.complete();
        }
      );
    return subject.asObservable();
  }


}



// WEBPACK FOOTER //
// C:/Clients/rent-hero/web-application/src/app/_shared/services/firebase/repair.service.ts